import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState<'en' | 'fr'>('en');

  const content = {
    en: {
      nav: {
        home: 'Home',
        about: 'About',
        training: 'Training',
        experience: 'Experience',
        skills: 'Skills',
        contact: 'Contact'
      },
      hero: {
        label: 'TALENT PROFILE',
        name: 'NAOMI',
        professions: ['Actress', 'Musical Theatre Performer', 'Dancer'],
        age: '10 years old',
        availability: 'Available Internationally'
      },
      about: {
        title: 'About',
        text1: 'Naomi is a young internationally trained actress and musical theatre performer with a strong background in theatre, dance, and live performance. She began her artistic journey in dance in 2018 and quickly transitioned into musical theatre and acting, developing a solid and disciplined performance foundation from an early age.',
        text2: 'She has performed in major stage productions and trained in renowned institutions across France, the UK, and Spain. Fluent in both French and English, Naomi is comfortable working in international environments and is available for professional projects worldwide.',
        text3: 'With a passion for storytelling and a natural stage presence, Naomi brings dedication, emotional depth, and professionalism to every role she undertakes.'
      },
      profile: {
        physical: 'Physical Profile',
        height: 'Height: 1m50',
        hair: 'Hair: Blonde',
        eyes: 'Eyes: Blue',
        age: 'Age: 10 years old',
        languages: 'Languages',
        french: 'French – Native',
        english: 'English – Fluent',
        availability: 'Availability',
        locations: ['France', 'United Kingdom', 'Spain', 'Ireland', 'International'],
        worldwide: 'WORLDWIDE'
      },
      training: {
        title: 'Training & Education',
        items: [
          {
            year: '2018',
            title: 'Dance Training',
            description: 'Foundation in dance, establishing core movement and performance skills'
          },
          {
            year: '2019',
            title: 'Musical Theatre',
            institution: 'Art Scene, Montpellier',
            description: 'First stage production: Alice in Wonderland'
          },
          {
            year: '2020',
            title: 'Theatre & Stage Performance',
            institution: 'Agence Améliorer, Montpellier',
            description: 'Stage production: Les Dix Commandements'
          },
          {
            year: '2020-2023',
            title: 'Acting, Singing & Piano',
            institution: 'Cours Florent, Montpellier',
            description: 'Comprehensive training in performance arts at one of France\'s most prestigious acting schools'
          },
          {
            year: '2023-Present',
            title: 'Theatre Training',
            institution: 'Mougins School / Theatre School in Grasse',
            description: 'Ongoing professional theatre training'
          },
          {
            year: '2023-Present',
            title: 'Performing Arts',
            institution: 'International School of Arts, Barcelona',
            description: 'Acting, Musical Theatre, Singing, Dance. Additional LAMDA certification'
          }
        ]
      },
      experience: {
        title: 'Experience',
        items: [
          'Musical Theatre productions',
          'Stage performances (theatre & musical roles)',
          'Ensemble and lead youth roles',
          'Live audience performances',
          'International training programs'
        ]
      },
      skills: {
        title: 'Skills',
        primary: 'Primary Skills',
        performance: 'Performance',
        additional: 'Additional',
        list: [
          { number: '01', name: 'Acting', desc: 'Theatre & Screen foundations' },
          { number: '02', name: 'Musical Theatre', desc: 'Stage performance & storytelling' },
          { number: '03', name: 'Singing', desc: 'Vocal performance' },
          { number: '04', name: 'Dance', desc: 'Movement & choreography' }
        ],
        performanceSkills: ['Stage Presence', 'Emotional Expression', 'Discipline'],
        additionalSkills: ['Piano', 'LAMDA Certified', 'Bilingual']
      },
      contact: {
        title: 'Let\'s Work Together',
        subtitle: 'For professional casting & representation enquiries',
        email: 'contact@la-naomi.fr',
        phone: '+33 7 66 14 97 01',
        button: 'Get in Touch'
      },
      footer: {
        description: 'Professional actress and musical theatre performer available for international projects.',
        quickLinks: 'Quick Links',
        languages: 'Languages',
        connect: 'Connect'
      }
    },
    fr: {
      nav: {
        home: 'Accueil',
        about: 'À Propos',
        training: 'Formation',
        experience: 'Expérience',
        skills: 'Compétences',
        contact: 'Contact'
      },
      hero: {
        label: 'PROFIL ARTISTIQUE',
        name: 'NAOMI',
        professions: ['Actrice', 'Comédienne Musicale', 'Danseuse'],
        age: '10 ans',
        availability: 'Disponible Internationalement'
      },
      about: {
        title: 'À Propos',
        text1: 'Naomi est une jeune actrice et comédienne musicale formée à l\'international avec une solide expérience en théâtre, danse et performance scénique. Elle a commencé son parcours artistique dans la danse en 2018 et s\'est rapidement tournée vers la comédie musicale et le théâtre, développant dès son plus jeune âge une base solide et disciplinée en performance.',
        text2: 'Elle a joué dans des productions scéniques majeures et s\'est formée dans des institutions renommées en France, au Royaume-Uni et en Espagne. Parlant couramment le français et l\'anglais, Naomi est à l\'aise dans les environnements internationaux et est disponible pour des projets professionnels dans le monde entier.',
        text3: 'Avec une passion pour la narration et une présence scénique naturelle, Naomi apporte dévouement, profondeur émotionnelle et professionnalisme à chaque rôle qu\'elle entreprend.'
      },
      profile: {
        physical: 'Profil Physique',
        height: 'Taille : 1m50',
        hair: 'Cheveux : Blonds',
        eyes: 'Yeux : Bleus',
        age: 'Âge : 10 ans',
        languages: 'Langues',
        french: 'Français – Langue maternelle',
        english: 'Anglais – Courant',
        availability: 'Disponibilité',
        locations: ['France', 'Royaume-Uni', 'Espagne', 'Irlande', 'International'],
        worldwide: 'MONDE ENTIER'
      },
      training: {
        title: 'Formation & Éducation',
        items: [
          {
            year: '2018',
            title: 'Formation en Danse',
            description: 'Base en danse, établissant les compétences fondamentales en mouvement et performance'
          },
          {
            year: '2019',
            title: 'Comédie Musicale',
            institution: 'Art Scene, Montpellier',
            description: 'Première production scénique : Alice au Pays des Merveilles'
          },
          {
            year: '2020',
            title: 'Théâtre & Performance Scénique',
            institution: 'Agence Améliorer, Montpellier',
            description: 'Production scénique : Les Dix Commandements'
          },
          {
            year: '2020-2023',
            title: 'Théâtre, Chant & Piano',
            institution: 'Cours Florent, Montpellier',
            description: 'Formation complète en arts de la scène dans l\'une des écoles de théâtre les plus prestigieuses de France'
          },
          {
            year: '2023-Présent',
            title: 'Formation Théâtrale',
            institution: 'Mougins School / École de Théâtre à Grasse',
            description: 'Formation théâtrale professionnelle en cours'
          },
          {
            year: '2023-Présent',
            title: 'Arts de la Scène',
            institution: 'International School of Arts, Barcelone',
            description: 'Théâtre, Comédie Musicale, Chant, Danse. Certification LAMDA supplémentaire'
          }
        ]
      },
      experience: {
        title: 'Expérience',
        items: [
          'Productions de comédie musicale',
          'Performances scéniques (rôles théâtraux et musicaux)',
          'Rôles d\'ensemble et rôles principaux jeunesse',
          'Performances devant public',
          'Programmes de formation internationaux'
        ]
      },
      skills: {
        title: 'Compétences',
        primary: 'Compétences Principales',
        performance: 'Performance',
        additional: 'Supplémentaires',
        list: [
          { number: '01', name: 'Théâtre', desc: 'Fondations scène & écran' },
          { number: '02', name: 'Comédie Musicale', desc: 'Performance scénique & narration' },
          { number: '03', name: 'Chant', desc: 'Performance vocale' },
          { number: '04', name: 'Danse', desc: 'Mouvement & chorégraphie' }
        ],
        performanceSkills: ['Présence Scénique', 'Expression Émotionnelle', 'Discipline'],
        additionalSkills: ['Piano', 'Certifiée LAMDA', 'Bilingue']
      },
      contact: {
        title: 'Travaillons Ensemble',
        subtitle: 'Pour les demandes de casting et de représentation professionnelle',
        email: 'contact@la-naomi.fr',
        phone: '+33 7 66 14 97 01',
        button: 'Contactez-moi'
      },
      footer: {
        description: 'Actrice professionnelle et comédienne musicale disponible pour des projets internationaux.',
        quickLinks: 'Liens Rapides',
        languages: 'Langues',
        connect: 'Contact'
      }
    }
  };

  const t = content[language];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#111111]/95 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/ac6eabdb4cc8c584e68459ed2e8db63e/ed08d5c22831ba34526d3bc2cc182003.png" 
              alt="Naomi Logo" 
              className="h-12 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.home}</button>
            <button onClick={() => scrollToSection('about')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.about}</button>
            <button onClick={() => scrollToSection('training')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.training}</button>
            <button onClick={() => scrollToSection('experience')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.experience}</button>
            <button onClick={() => scrollToSection('skills')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.skills}</button>
            <button onClick={() => scrollToSection('contact')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide whitespace-nowrap cursor-pointer">{t.nav.contact}</button>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 ml-4 border-l border-[#C8A45D]/30 pl-4">
              <button 
                onClick={() => setLanguage('en')}
                className={`text-sm tracking-wide whitespace-nowrap cursor-pointer transition-colors ${language === 'en' ? 'text-[#C8A45D] font-semibold' : 'text-[#F6F4EF] hover:text-[#C8A45D]'}`}
              >
                EN
              </button>
              <span className="text-[#C8A45D]/30">|</span>
              <button 
                onClick={() => setLanguage('fr')}
                className={`text-sm tracking-wide whitespace-nowrap cursor-pointer transition-colors ${language === 'fr' ? 'text-[#C8A45D] font-semibold' : 'text-[#F6F4EF] hover:text-[#C8A45D]'}`}
              >
                FR
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-[#F6F4EF] w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-[#111111] border-t border-[#C8A45D]/20">
            <div className="px-8 py-6 flex flex-col gap-4">
              <button onClick={() => scrollToSection('home')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.home}</button>
              <button onClick={() => scrollToSection('about')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.about}</button>
              <button onClick={() => scrollToSection('training')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.training}</button>
              <button onClick={() => scrollToSection('experience')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.experience}</button>
              <button onClick={() => scrollToSection('skills')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.skills}</button>
              <button onClick={() => scrollToSection('contact')} className="text-[#F6F4EF] hover:text-[#C8A45D] transition-colors text-sm tracking-wide text-left whitespace-nowrap cursor-pointer">{t.nav.contact}</button>
              <div className="flex items-center gap-3 pt-4 border-t border-[#C8A45D]/20">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`text-sm tracking-wide whitespace-nowrap cursor-pointer ${language === 'en' ? 'text-[#C8A45D] font-semibold' : 'text-[#F6F4EF]'}`}
                >
                  EN
                </button>
                <span className="text-[#C8A45D]/30">|</span>
                <button 
                  onClick={() => setLanguage('fr')}
                  className={`text-sm tracking-wide whitespace-nowrap cursor-pointer ${language === 'fr' ? 'text-[#C8A45D] font-semibold' : 'text-[#F6F4EF]'}`}
                >
                  FR
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen bg-[#111111] pt-24 pb-16 px-8 flex items-center">
        <div className="max-w-[1400px] mx-auto w-full grid md:grid-cols-2 gap-12 items-center">
          {/* Left - Portrait */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-[#C8A45D] shadow-2xl">
              <img 
                src="https://static.readdy.ai/image/ac6eabdb4cc8c584e68459ed2e8db63e/101b2fe1d94d889cb1f07e85b3fbe42e.jpeg" 
                alt="Naomi - Professional Headshot" 
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Right - Text */}
          <div className="flex flex-col justify-center space-y-8">
            <div className="text-[#C8A45D] text-xs tracking-[0.3em] uppercase font-light">
              {t.hero.label}
            </div>
            
            <h1 className="font-serif text-[#F6F4EF] text-7xl md:text-8xl tracking-[0.15em] font-light">
              {t.hero.name}
            </h1>

            <div className="space-y-3">
              {t.hero.professions.map((profession, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#C8A45D]"></div>
                  <span className="text-[#B8B8B8] text-lg font-light tracking-wide">{profession}</span>
                </div>
              ))}
            </div>

            <div className="pt-6 space-y-2 text-sm text-[#B8B8B8] tracking-wide">
              <div>{t.hero.age}</div>
              <div>{t.hero.availability}</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-[#C8A45D] text-2xl"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-[#F6F4EF] py-32 px-8">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-5 gap-16">
          <div className="md:col-span-2">
            <h2 className="font-serif text-[#111111] text-6xl mb-6">{t.about.title}</h2>
            <div className="w-20 h-0.5 bg-[#C8A45D]"></div>
          </div>

          <div className="md:col-span-3 space-y-8 text-[#111111] text-base leading-relaxed">
            <p className="first-letter:text-5xl first-letter:font-serif first-letter:text-[#C8A45D] first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:mt-1">
              {t.about.text1}
            </p>
            <p>{t.about.text2}</p>
            <p className="italic text-lg text-[#C8A45D] font-serif">
              "{t.about.text3}"
            </p>
          </div>
        </div>
      </section>

      {/* Profile Details Cards */}
      <section className="bg-[#F6F4EF] pb-32 px-8">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-10 gap-6">
          {/* Physical Profile Card */}
          <div className="md:col-span-4 bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-user-line text-[#C8A45D] text-2xl"></i>
              </div>
              <h3 className="font-serif text-[#111111] text-2xl">{t.profile.physical}</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C8A45D] mt-2"></div>
                <span className="text-[#111111]">{t.profile.height}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C8A45D] mt-2"></div>
                <span className="text-[#111111]">{t.profile.hair}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C8A45D] mt-2"></div>
                <span className="text-[#111111]">{t.profile.eyes}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C8A45D] mt-2"></div>
                <span className="text-[#111111]">{t.profile.age}</span>
              </div>
            </div>
            <div className="mt-8 flex justify-center">
              <div className="w-20 h-20 rounded-full border-2 border-[#C8A45D] overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/ac6eabdb4cc8c584e68459ed2e8db63e/bcd8b550b09d82e5af52e0cf17f4f0ba.jpeg" 
                  alt="Naomi" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Languages Card */}
          <div className="md:col-span-3 bg-[#C8A45D] rounded-2xl p-8 shadow-lg md:mt-16">
            <h3 className="font-serif text-white text-2xl mb-6">{t.profile.languages}</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-flag-line text-white text-xl"></i>
                </div>
                <span className="text-white">{t.profile.french}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-flag-line text-white text-xl"></i>
                </div>
                <span className="text-white">{t.profile.english}</span>
              </div>
            </div>
          </div>

          {/* Availability Card */}
          <div className="md:col-span-3 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="font-serif text-[#111111] text-2xl mb-6">{t.profile.availability}</h3>
            <div className="space-y-3">
              {t.profile.locations.map((location, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-[#C8A45D] text-lg"></i>
                  </div>
                  <span className="text-[#111111] text-sm">{location}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <div className="font-serif text-[#C8A45D] text-3xl tracking-wider">{t.profile.worldwide}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Training & Education Timeline */}
      <section id="training" className="bg-gradient-to-b from-[#F6F4EF] to-[#2A2A2A] py-32 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-white text-7xl mb-20">
            {t.training.title}
            <div className="w-32 h-1 bg-[#C8A45D] mt-6"></div>
          </h2>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-0 md:left-24 top-0 bottom-0 w-0.5 bg-[#C8A45D]"></div>

            <div className="space-y-20">
              {t.training.items.map((item, index) => (
                <div key={index} className={`relative flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-start`}>
                  {/* Timeline Node */}
                  <div className="absolute left-0 md:left-24 w-4 h-4 rounded-full bg-[#C8A45D] transform -translate-x-1.5"></div>

                  {/* Year */}
                  <div className="md:w-24 flex-shrink-0 pl-8 md:pl-0">
                    <div className="font-serif text-[#C8A45D] text-5xl">{item.year}</div>
                  </div>

                  {/* Content */}
                  <div className={`flex-1 bg-[#1A1A1A]/80 rounded-2xl p-8 backdrop-blur-sm hover:bg-[#1A1A1A] transition-all duration-300 ${index % 2 === 0 ? 'md:ml-32' : 'md:mr-32'} border-l-4 border-transparent hover:border-[#C8A45D]`}>
                    <h3 className="font-bold text-white text-xl mb-2">{item.title}</h3>
                    {item.institution && (
                      <div className="text-[#C8A45D] text-sm mb-3">{item.institution}</div>
                    )}
                    <p className="text-[#B8B8B8] text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="bg-[#2A2A2A] py-32 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-white text-6xl mb-16 text-center">{t.experience.title}</h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-[900px] mx-auto">
            {t.experience.items.map((item, index) => (
              <div key={index} className="bg-[#1A1A1A] rounded-xl p-6 border border-[#C8A45D]/20 hover:border-[#C8A45D] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-[#C8A45D] text-xl"></i>
                  </div>
                  <span className="text-white text-base">{item}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="bg-[#111111] py-32 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-white text-6xl mb-16 text-center">{t.skills.title}</h2>

          <div className="grid md:grid-cols-12 gap-6">
            {/* Primary Skills - Large Card */}
            <div className="md:col-span-7 bg-[#1A1A1A] rounded-2xl p-10">
              <h3 className="font-serif text-[#C8A45D] text-2xl mb-8">{t.skills.primary}</h3>
              <div className="space-y-8">
                {t.skills.list.map((skill) => (
                  <div key={skill.number} className="flex items-start gap-6">
                    <div className="font-serif text-[#C8A45D] text-4xl font-light">{skill.number}</div>
                    <div>
                      <h4 className="text-white text-2xl mb-1">{skill.name}</h4>
                      <p className="text-[#B8B8B8] text-sm">{skill.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-12 pt-8 border-t border-[#C8A45D]/20">
                <div className="flex items-center gap-4 text-[#C8A45D]">
                  <i className="ri-film-line text-3xl"></i>
                  <i className="ri-music-line text-3xl"></i>
                  <i className="ri-mic-line text-3xl"></i>
                </div>
              </div>
            </div>

            {/* Right Column - Two Stacked Cards */}
            <div className="md:col-span-5 space-y-6">
              {/* Performance Card */}
              <div className="bg-[#C8A45D] rounded-2xl p-8">
                <h3 className="font-serif text-[#111111] text-2xl mb-6">{t.skills.performance}</h3>
                <div className="space-y-4">
                  {t.skills.performanceSkills.map((skill, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#C8A45D] text-xl"></i>
                      </div>
                      <span className="text-[#111111] font-medium">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Skills Card */}
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-serif text-[#111111] text-2xl mb-6">{t.skills.additional}</h3>
                <div className="flex flex-wrap gap-3">
                  {t.skills.additionalSkills.map((skill, index) => (
                    <div key={index} className="px-5 py-2 rounded-full border-2 border-[#C8A45D] text-[#111111] text-sm font-medium whitespace-nowrap">
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact" className="relative min-h-[70vh] flex items-center justify-center px-8">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/61108e9a94429c959be31125f94b564f.jpg" 
            alt="Theatre Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/70 via-[#111111]/60 to-[#111111]/70"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto w-full">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12">
            <div>
              <h2 className="font-serif text-white text-6xl md:text-7xl mb-8 drop-shadow-lg lowercase">
                {t.contact.title}
              </h2>
              <p className="text-white/90 text-lg mb-8 drop-shadow">{t.contact.subtitle}</p>
              <button 
                onClick={() => window.location.href = `mailto:${t.contact.email}`}
                className="group flex items-center gap-4 bg-white hover:bg-[#F6F4EF] text-[#111111] px-8 py-4 rounded-full transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <span className="font-medium">{t.contact.button}</span>
                <div className="w-12 h-12 rounded-full bg-[#C8A45D] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </div>
              </button>
            </div>

            <div className="text-right space-y-3">
              <div className="text-white text-lg drop-shadow-lg">
                <a href={`mailto:${t.contact.email}`} className="hover:text-[#C8A45D] transition-colors cursor-pointer">
                  {t.contact.email}
                </a>
              </div>
              <div className="text-white text-lg drop-shadow-lg">
                <a href={`tel:${t.contact.phone}`} className="hover:text-[#C8A45D] transition-colors cursor-pointer">
                  {t.contact.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111111] pt-20 pb-12 px-8 border-t border-[#C8A45D]/20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Column 1 - Brand */}
            <div>
              <div className="font-serif text-[#C8A45D] text-3xl mb-4">NAOMI</div>
              <p className="text-[#B8B8B8] text-sm leading-relaxed">
                {t.footer.description}
              </p>
            </div>

            {/* Column 2 - Quick Links */}
            <div>
              <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-6">{t.footer.quickLinks}</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-[#B8B8B8] hover:text-[#C8A45D] text-sm transition-colors cursor-pointer">{t.nav.home}</button>
                <button onClick={() => scrollToSection('about')} className="block text-[#B8B8B8] hover:text-[#C8A45D] text-sm transition-colors cursor-pointer">{t.nav.about}</button>
                <button onClick={() => scrollToSection('training')} className="block text-[#B8B8B8] hover:text-[#C8A45D] text-sm transition-colors cursor-pointer">{t.nav.training}</button>
                <button onClick={() => scrollToSection('experience')} className="block text-[#B8B8B8] hover:text-[#C8A45D] text-sm transition-colors cursor-pointer">{t.nav.experience}</button>
                <button onClick={() => scrollToSection('skills')} className="block text-[#B8B8B8] hover:text-[#C8A45D] text-sm transition-colors cursor-pointer">{t.nav.skills}</button>
              </div>
            </div>

            {/* Column 3 - Languages */}
            <div>
              <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-6">{t.footer.languages}</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <i className="ri-flag-line text-[#C8A45D]"></i>
                  <span className="text-[#B8B8B8] text-sm">{t.profile.french}</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-flag-line text-[#C8A45D]"></i>
                  <span className="text-[#B8B8B8] text-sm">{t.profile.english}</span>
                </div>
              </div>
            </div>

            {/* Column 4 - Connect */}
            <div>
              <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-6">{t.footer.connect}</h4>
              <div className="space-y-3">
                <a href={`mailto:${t.contact.email}`} className="block text-white text-sm hover:text-[#C8A45D] transition-colors cursor-pointer">
                  {t.contact.email}
                </a>
                <a href={`tel:${t.contact.phone}`} className="block text-white text-sm hover:text-[#C8A45D] transition-colors cursor-pointer">
                  {t.contact.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Signature */}
          <div className="relative h-32 flex items-center justify-center overflow-hidden">
            <div className="font-serif text-[#C8A45D]/30 text-[120px] tracking-[0.2em] font-light">
              NAOMI
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center pt-8 border-t border-[#C8A45D]/10">
            <p className="text-[#B8B8B8] text-xs">
              © {new Date().getFullYear()} Naomi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
